import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { useProviderAuth } from '@/context/ProviderAuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Eye, EyeOff, Mail, Lock } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Login = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const { login: providerLogin } = useProviderAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetLoading, setResetLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const normalizedEmail = email.toLowerCase().trim();

    try {
      // Single unified login call — no 401 console errors
      const res = await axios.post(`${API}/auth/unified-login`, { email: normalizedEmail, password });
      const { user_type, token: authToken } = res.data;

      if (user_type === 'admin') {
        localStorage.setItem('adminToken', authToken);
        localStorage.setItem('adminData', JSON.stringify(res.data.admin));
        toast.success('Admin login successful!');
        navigate('/admin/dashboard');
      } else if (user_type === 'agent' || user_type === 'broker') {
        login(authToken, res.data.agent);
        toast.success('Welcome back!');
        navigate(user_type === 'broker' ? '/broker' : '/dashboard');
      } else if (user_type === 'provider') {
        providerLogin(authToken, res.data.provider);
        toast.success('Welcome back!');
        navigate('/provider/dashboard');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!resetEmail) {
      toast.error('Please enter your email address');
      return;
    }

    setResetLoading(true);
    try {
      const res = await axios.post(`${API}/auth/request-password-reset`, null, {
        params: { email: resetEmail.toLowerCase().trim() }
      });
      toast.success(res.data.message || 'A password reset link has been sent to your email.');
      setShowForgotPassword(false);
      setResetEmail('');
    } catch (error) {
      toast.error('Failed to submit reset request. Please try again.');
    } finally {
      setResetLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative flex items-center justify-center p-4">
      {/* Background Image */}
      <div
        className="absolute inset-0 z-0"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1762117360871-f11fbad00ee1?crop=entropy&cs=srgb&fm=jpg&q=85)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          filter: 'brightness(0.5)'
        }}
      />

      {/* Login Card */}
      <div className="glass-card rounded-2xl p-8 md:p-12 w-full max-w-md z-10">
        <div className="text-center mb-8">
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#1A4D2E] mb-2" style={{ fontFamily: '"Playfair Display", serif' }}>Open House</h1>
          <p className="text-stone-600">Sign in to manage your events</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <Label className="text-sm font-semibold uppercase tracking-wider text-stone-500 mb-2 block">Email</Label>
            <Input
              data-testid="login-email-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="h-14 bg-stone-50 border-2 border-stone-200 focus:border-[#1A4D2E] focus:ring-0 rounded-lg text-lg px-4"
              required
            />
          </div>

          <div>
            <Label className="text-sm font-semibold uppercase tracking-wider text-stone-500 mb-2 block">Password</Label>
            <div className="relative">
              <Input
                data-testid="login-password-input"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="h-14 bg-stone-50 border-2 border-stone-200 focus:border-[#1A4D2E] focus:ring-0 rounded-lg text-lg px-4 pr-12"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600"
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
          </div>

          <Button
            data-testid="login-submit-button"
            type="submit"
            disabled={loading}
            className="w-full bg-[#1A4D2E] text-white rounded-full h-14 text-lg hover:bg-[#143D24] transition-transform hover:scale-105 active:scale-95 shadow-lg shadow-green-900/20"
          >
            {loading ? 'Signing in...' : 'Sign In'}
          </Button>

          {/* Forgot Password Link */}
          <div className="text-center mt-4">
            <button
              type="button"
              onClick={() => setShowForgotPassword(true)}
              className="text-[#1A4D2E] text-sm font-semibold hover:underline"
            >
              Forgot Password?
            </button>
          </div>
        </form>

        <p className="text-center mt-6 text-stone-600">
          Looking for an agent or service provider?{' '}
          <Link to="/search" className="text-[#1A4D2E] font-semibold hover:underline flex items-center justify-center gap-1">
            <span>Search Professionals</span>
            <span className="text-xl"></span>
          </Link>
        </p>

        <p className="text-center mt-4">
          <Link to="/" className="text-stone-500 hover:text-[#1A4D2E] text-sm hover:underline" data-testid="home-link">
            Back to Home
          </Link>
        </p>
      </div>

      {/* Forgot Password Dialog */}
      <Dialog open={showForgotPassword} onOpenChange={setShowForgotPassword}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-[#1A4D2E]">Reset Password</DialogTitle>
            <DialogDescription>
              Enter your email address and we'll send you a link to reset your password.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleForgotPassword} className="space-y-4">
            <div>
              <Label htmlFor="reset-email" className="text-sm font-semibold uppercase tracking-wider text-stone-500 mb-2 block">
                Email Address
              </Label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-stone-400" size={20} />
                <Input
                  id="reset-email"
                  type="email"
                  placeholder="your.email@example.com"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  className="pl-12 h-14 bg-stone-50 border-2 border-stone-200 focus:border-[#1A4D2E] focus:ring-0 rounded-lg"
                  required
                />
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-sm text-blue-800">
                You'll receive an email with a link to set a new password. The link expires in 1 hour.
              </p>
            </div>

            <div className="flex gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowForgotPassword(false)}
                className="flex-1"
                disabled={resetLoading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={resetLoading}
                className="flex-1 bg-[#1A4D2E] text-white hover:bg-[#143D24]"
              >
                {resetLoading ? 'Sending...' : 'Send Reset Link'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Login;
